/**
 * Assignment Response Rates Report Page
 *
 * Shows assignment confirm/decline trends over time.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAssignmentResponseRates, downloadReportCSV, AssignmentResponseRatesData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange } from '../../../components/reports';

export default function AssignmentResponseRatesPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<AssignmentResponseRatesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAssignmentResponseRates({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/assignment-response-rates',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'assignment-response-rates.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate totals
  const totals = report.reduce(
    (acc, row) => ({
      totalAssignments: acc.totalAssignments + row.totalAssignments,
      confirmedCount: acc.confirmedCount + row.confirmedCount,
      declinedCount: acc.declinedCount + row.declinedCount,
      pendingCount: acc.pendingCount + row.pendingCount,
    }),
    { totalAssignments: 0, confirmedCount: 0, declinedCount: 0, pendingCount: 0 }
  );

  const overallConfirmRate = totals.totalAssignments > 0
    ? Math.round((totals.confirmedCount / totals.totalAssignments) * 100)
    : 0;
  const overallDeclineRate = totals.totalAssignments > 0
    ? Math.round((totals.declinedCount / totals.totalAssignments) * 100)
    : 0;

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Assignment Response Rates</h1>
        <p className="text-gray-600 mt-1">Confirm and decline trends by period</p>
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No assignment response data for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirmed</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Declined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirm Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Decline Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.period} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{row.period}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.totalAssignments}</td>
                    <td className="px-6 py-4 text-right text-sm text-green-600">{row.confirmedCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-red-600">{row.declinedCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-yellow-600">{row.pendingCount}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        row.confirmRate >= 80 ? 'bg-green-100 text-green-800' :
                        row.confirmRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {row.confirmRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        row.declineRate <= 10 ? 'bg-green-100 text-green-800' :
                        row.declineRate <= 25 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {row.declineRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">{totals.totalAssignments}</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-green-600">{totals.confirmedCount}</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-red-600">{totals.declinedCount}</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-yellow-600">{totals.pendingCount}</td>
                  <td className="px-6 py-3 text-right">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      overallConfirmRate >= 80 ? 'bg-green-100 text-green-800' :
                      overallConfirmRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {overallConfirmRate}%
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      overallDeclineRate <= 10 ? 'bg-green-100 text-green-800' :
                      overallDeclineRate <= 25 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {overallDeclineRate}%
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
